import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { jwtDecode } from 'jwt-decode';
import { of } from 'rxjs';
import { catchError, map, mergeMap, tap } from 'rxjs/operators';
import { VAR_ACCESS_TOKEN, VAR_REFRESH_TOKEN } from '../../../../../shared/config/consts';
import { ToastService } from '../../components/common/mb-toast/mb-toast.component';
import { AuthService, UserService } from '../../core/services';
import { AuthActions } from './auth.actions';
import {citizenRouteNames} from '../../pages/citizen/citizen.routes';

// Define the JwtPayload interface
interface JwtPayload {
  roles: string[];
  permissions: string[];
  exp: number;
  iat: number;
  sub: string;
}

@Injectable()
export class AuthEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  private router = inject(Router);
  private store = inject(Store);
  private userService = inject(UserService);
  private toastService = inject(ToastService);

  login$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.login),
      mergeMap(({ email, password }) =>
        this.authService.login(email, password).pipe(
          map((response) => {
            return AuthActions.loginSuccess({
              message: response.message,
              token: response.payload.accessToken,
              refreshToken: response.payload.refreshToken,
            });
          }),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(AuthActions.loginFailure({ error: errorMessage }));
          })
        )
      )
    );
  });

  loginAdmin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.loginAdmin),
      mergeMap(({ email, password }) =>
        this.authService.loginAdmin(email, password).pipe(
          map((response) => {
            console.log(response);
            return AuthActions.loginAdminSuccess({
              message: response.message,
              token: response.payload.accessToken,
              refreshToken: response.payload.refreshToken,
            });
          }),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(AuthActions.loginAdminFailure({ error: errorMessage }));
          })
        )
      )
    );
  });

  loginAdminSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.loginAdminSuccess),
        tap(({ token }) => {
          console.log('loginAdminSuccess');
          localStorage.setItem(VAR_ACCESS_TOKEN, token);
          const decodedToken = jwtDecode<JwtPayload>(token);

          const userRoles = decodedToken.roles;
          if (userRoles.includes('SUPER_ADMIN')) {
            localStorage.setItem('userType', 'SUPER_ADMIN');
            this.router.navigate(['/dashboard/complaints']);
          } else {
            localStorage.setItem('userType', 'ADMIN');
            this.router.navigate(['/dashboard/cases']);
          }
          if (userRoles.includes('ADMIN')) {
            this.store.dispatch(AuthActions.getCurrentUser());
          } else {
            // TODO: Handle admin login failure
          }
        })
      );
    },
    { dispatch: false }
  );

  loginAdminFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.loginAdminFailure),
        tap(({ error }) => {
          console.error(error);
        })
      );
    },
    { dispatch: false }
  );

  loginSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.loginSuccess),
        tap(({ token, refreshToken }) => {
          localStorage.setItem(VAR_ACCESS_TOKEN, token);
          localStorage.setItem(VAR_REFRESH_TOKEN, refreshToken);
          const decodedToken = jwtDecode<JwtPayload>(token);
          const userRoles = decodedToken.roles;

          if (userRoles.includes('CITIZEN')) {
            this.store.dispatch(AuthActions.getCurrentUser());
            window.location.href = citizenRouteNames.complaints;
          } else {
            // TODO: Handle citizen login failure
          }
        })
      );
    },
    { dispatch: false }
  );

  register$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.register),
      mergeMap(({ requestBody }) =>
        this.authService.register(requestBody).pipe(
          map((response) =>
            AuthActions.registerSuccess({
              message: response.message,
            })
          ),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(AuthActions.registerFailure({ error: errorMessage }));
          })
        )
      )
    );
  });

  registerSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.registerSuccess),
        tap(({ message }) => {
          this.toastService.show(
            message || 'Registration successful! Please log in.',
            'success'
          );
          this.router.navigate([citizenRouteNames.auth]);
        })
      );
    },
    { dispatch: false }
  );

  logout$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.logout),
      mergeMap(() =>
        this.authService.logout().pipe(
          map(() => AuthActions.logoutSuccess()),
          catchError(() => {
            return of(AuthActions.logoutSuccess());
          })
        )
      )
    );
  });

  logoutSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        tap(() => {
          localStorage.clear();
          window.location.href = citizenRouteNames.auth;
        })
      );
    },
    { dispatch: false }
  );

  getCurrentUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.getCurrentUser),
      mergeMap(() => {
        const token = localStorage.getItem(VAR_ACCESS_TOKEN);
        if (!token) {
          return of(
            AuthActions.getCurrentUserFailure({ error: 'No token found' })
          );
        }

        return this.userService.getMe().pipe(
          map((response) => {
            const organizationId = response.payload.organizationId;
            if (organizationId) {
              localStorage.setItem('organizationId', organizationId);
            }
            const names = response.payload.fullName.split(' ');
            const user = {
              id:response.payload.id,
              email: response.payload.email,
              firstName: names[0] || '',
              lastName: names[1] || '',
              fullName: response.payload.fullName,
              phoneNumber: response.payload.phoneNumber,
              nationalIdNumber: response.payload.nationalIdNumber,
              gender: response.payload.gender,
              profilePictureUrl: response.payload.profilePictureUrl,
            };
            return AuthActions.getCurrentUserSuccess({ user });
          }),
          catchError((error) => {
            const errorMessage =
              error.error?.message || error.message || 'An error occurred';
            return of(
              AuthActions.getCurrentUserFailure({ error: errorMessage })
            );
          })
        );
      })
    );
  });

  getCurrentUserFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.getCurrentUserFailure),
      );
    },
    { dispatch: false }
  );
}
